import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Icon } from "@iconify/react";
import ProductApi from "../Api/ProductApi";
import StockApi from "../Api/StockApi";
import AttributeApi from "../Api/AttributeApi";
import usePermissions from "../hook/usePermissions";
import { toast } from "react-toastify";

const ProductStockUpdateLayer = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = usePermissions();

    const [loading, setLoading] = useState(true);
    const [stockLoading, setStockLoading] = useState(false);
    const [product, setProduct] = useState(null);

    const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
    const [stockType, setStockType] = useState("add"); // "add" or "reduce"
    const [stockQty, setStockQty] = useState("");
    const [stockDesc, setStockDesc] = useState("");
    const [attributesMap, setAttributesMap] = useState({});
    const [errors, setErrors] = useState({});

    const handleSelectVariant = (idx) => {
        setSelectedVariantIndex(idx);
        setErrors({});
    };

    useEffect(() => {
        const init = async () => {
            await fetchAttributes();
            await fetchProductDetails();
        };
        init();
    }, [id]);

    const fetchAttributes = async () => {
        try {
            const res = await AttributeApi.getAll(0, 500, "");
            if (res.status && res.response.data) {
                const map = {};
                res.response.data.forEach(attr => {
                    map[attr._id] = attr.name;
                });
                setAttributesMap(map);
            }
        } catch (e) {
            console.error("Error fetching attributes:", e);
        }
    };

    const fetchProductDetails = async () => {
        setLoading(true);
        try {
            const res = await ProductApi.getById(id);
            if (res.status) {
                const freshProduct = res.response.data || res.response;
                setProduct(freshProduct);
                if (freshProduct.attributes && freshProduct.attributes.length > 0) {
                    setSelectedVariantIndex(0); // Default select the first variant
                }
            } else {
                toast.error("Failed to load product details");
            }
        } catch (e) {
            console.error("Error loading product:", e);
            toast.error("Error loading product details");
        } finally {
            setLoading(false);
        }
    };

    const getAttributeString = (combination) => {
        if (!combination || !Array.isArray(combination)) return "Default";
        const str = combination.map(c => c.value).filter(Boolean).join(", ");
        return str || "Default";
    };

    const handleStockUpdateSubmit = async (e) => {
        e.preventDefault();
        if (!product) {
            toast.error("Product data not loaded");
            return;
        }
        if (selectedVariantIndex === null || !product.attributes || !product.attributes[selectedVariantIndex]) {
            toast.error("Please select a variant");
            return;
        }

        const selectedVariant = product.attributes[selectedVariantIndex];
        const qtyNum = Number(stockQty);
        if (!stockQty || isNaN(qtyNum) || qtyNum <= 0) {
            setErrors({ stockQty: "Adjustment quantity is required and must be greater than 0." });
            return;
        }

        const currentStock = Number(selectedVariant.stock) || 0;
        let newStock = currentStock;
        if (stockType === "add") {
            newStock = currentStock + qtyNum;
        } else {
            if (currentStock < qtyNum) {
                toast.error(`Cannot reduce stock below 0. Current stock is ${currentStock}.`);
                return;
            }
            newStock = currentStock - qtyNum;
        }

        setStockLoading(true);
        try {
            const variantPayload = {
                ...selectedVariant,
                newStock: newStock,
                variantLabel: getAttributeString(selectedVariant.combination)
            };

            const res = await StockApi.updateManual({
                productId: product._id || product.id,
                variants: [variantPayload],
                description: stockDesc || `Stock ${stockType === "add" ? "added" : "reduced"} from list`
            });

            if (res.status) {
                toast.success("Stock updated successfully");
                setStockQty("");
                setStockDesc("");
                setErrors({});
                fetchProductDetails(); // Re-fetch to load fresh stock levels
            } else {
                toast.error("Failed to update stock");
            }
        } catch (err) {
            console.error("Error updating stock:", err);
            toast.error("An error occurred while updating stock");
        } finally {
            setStockLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="card h-100 p-24 radius-12 text-center py-5">
                <div className="text-secondary-light">Loading product inventory data...</div>
            </div>
        );
    }

    return (
        <div className="card h-100 p-0 radius-12">
            <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3">
                <div>
                    <h6 className="text-primary-600 pb-2 mb-0">Adjust Inventory Stock</h6>
                    <span className="text-sm text-secondary-light">Adjust stock levels for specific variant combinations</span>
                </div>
                <Link to="/products" className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-1 radius-8 px-16 h-40-px">
                    <Icon icon="solar:arrow-left-outline" /> Back to Products
                </Link>
            </div>

            <div className="card-body p-24">
                {product ? (
                    <form onSubmit={handleStockUpdateSubmit} noValidate>
                        <div className="mb-24 p-16 border rounded bg-light-50">
                            <h5 className="text-md fw-bold text-secondary-light mb-1">
                                Product: <span className="text-primary-600">{product.name}</span>
                            </h5>
                            <span className="text-xs text-secondary-light">
                                SKU base: <strong>{product.attributes?.[0]?.sku?.split('-')?.[0] || "N/A"}</strong>
                            </span>
                        </div>

                        <div className="row gy-4">
                            {/* Variant Selection Column */}
                            <div className="col-12">
                                <label className="form-label fw-bold text-secondary-light mb-8">1. Select Variant Combination</label>
                                <div className="table-responsive border rounded scroll-sm">
                                    <table className="table bordered-table sm-table mb-0">
                                        <thead className="bg-light sticky-top">
                                            <tr>
                                                <th scope="col" className="text-center py-12" style={{ color: "black", width: "80px", fontWeight: "600" }}>Select</th>
                                                <th scope="col" className="py-12" style={{ color: "black", fontWeight: "600" }}>Variant Combination</th>
                                                <th scope="col" className="py-12" style={{ color: "black", fontWeight: "600" }}>SKU</th>
                                                <th scope="col" className="text-end py-12 pe-24" style={{ color: "black", fontWeight: "600" }}>Current Stock</th>
                                                <th scope="col" className="text-center py-12" style={{ color: "black", width: "180px", fontWeight: "600" }}>Logs</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {product.attributes && product.attributes.length > 0 ? (
                                                product.attributes.map((attr, idx) => {
                                                    const label = getAttributeString(attr.combination);
                                                    const isSelected = selectedVariantIndex === idx;
                                                    return (
                                                        <tr 
                                                            key={attr.sku || idx} 
                                                            onClick={() => handleSelectVariant(idx)}
                                                            style={{ 
                                                                cursor: "pointer", 
                                                                backgroundColor: isSelected ? "#eef5f0" : "transparent",
                                                                transition: "background-color 0.15s ease"
                                                            }}
                                                        >
                                                            <td className="text-center align-middle py-12">
                                                                <div className="d-flex justify-content-center align-items-center">
                                                                    {isSelected ? (
                                                                        <Icon icon="solar:check-circle-bold" style={{ fontSize: "22px", color: "#287f44" }} />
                                                                    ) : (
                                                                        <div 
                                                                            style={{ 
                                                                                width: "20px", 
                                                                                height: "20px", 
                                                                                borderRadius: "50%", 
                                                                                border: "2px solid #475569", 
                                                                                backgroundColor: "#ffffff",
                                                                                boxSizing: "border-box"
                                                                            }} 
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td className="fw-medium align-middle py-12">{label}</td>
                                                            <td className="align-middle py-12">{attr.sku}</td>
                                                            <td className="text-end align-middle fw-semibold text-secondary-light py-12 pe-24">{attr.stock}</td>
                                                            <td className="text-center align-middle py-12" onClick={(e) => e.stopPropagation()}>
                                                                <Link 
                                                                    to={`/products/stock-logs/${product._id || product.id}/${attr.sku}?valueIds=${attr.combination?.map(c => c.valueId).filter(Boolean).join(",")}`}
                                                                    className="btn btn-outline-primary btn-sm radius-8 px-12 py-6 d-inline-flex align-items-center gap-1"
                                                                    style={{ fontSize: "12px" }}
                                                                >
                                                                    <Icon icon="solar:document-text-bold-duotone" />
                                                                    View Stock Logs
                                                                </Link>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan="5" className="text-center py-4 text-secondary-light">
                                                        No variants found for this product.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {selectedVariantIndex !== null && product.attributes?.[selectedVariantIndex] && (
                                <>
                                    {/* Action Type */}
                                    <div className="col-md-6 mt-32">
                                        <label className="form-label fw-bold text-secondary-light mb-8">2. Stock Action Type</label>
                                        <div className="d-flex gap-3">
                                            <button 
                                                type="button"
                                                className="flex-fill p-12 border rounded-8 d-flex align-items-center justify-content-center fw-semibold" 
                                                onClick={() => {
                                                    setStockType("add");
                                                    setErrors({});
                                                }}
                                                style={{ 
                                                    cursor: "pointer", 
                                                    backgroundColor: stockType === "add" ? "#eef5f0" : "transparent",
                                                    borderColor: stockType === "add" ? "#364e3d" : "#dee2e6",
                                                    color: stockType === "add" ? "#364e3d" : "#7b7b7b",
                                                    transition: "all 0.2s",
                                                    height: "50px",
                                                    outline: "none",
                                                    borderWidth: "2px"
                                                }}
                                            >
                                                <Icon 
                                                    icon={stockType === "add" ? "solar:add-circle-bold" : "solar:add-circle-linear"} 
                                                    className="me-2" 
                                                    style={{ fontSize: "20px", color: stockType === "add" ? "#287f44" : "#7b7b7b" }} 
                                                />
                                                Add Stock (+)
                                            </button>
                                            <button 
                                                type="button"
                                                className="flex-fill p-12 border rounded-8 d-flex align-items-center justify-content-center fw-semibold" 
                                                onClick={() => {
                                                    setStockType("reduce");
                                                    setErrors({});
                                                }}
                                                style={{ 
                                                    cursor: "pointer", 
                                                    backgroundColor: stockType === "reduce" ? "#fdf2f2" : "transparent",
                                                    borderColor: stockType === "reduce" ? "#d93939" : "#dee2e6",
                                                    color: stockType === "reduce" ? "#d93939" : "#7b7b7b",
                                                    transition: "all 0.2s",
                                                    height: "50px",
                                                    outline: "none",
                                                    borderWidth: "2px"
                                                }}
                                            >
                                                <Icon 
                                                    icon={stockType === "reduce" ? "solar:minus-circle-bold" : "solar:minus-circle-linear"} 
                                                    className="me-2" 
                                                    style={{ fontSize: "20px", color: stockType === "reduce" ? "#d93939" : "#7b7b7b" }} 
                                                />
                                                Reduce Stock (-)
                                            </button>
                                        </div>
                                    </div>

                                    {/* Quantity */}
                                    <div className="col-md-6 mt-32">
                                        <label className="form-label fw-bold text-secondary-light mb-8">3. Adjustment Quantity</label>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            className="form-control radius-8 h-50-px"
                                            placeholder="Enter adjustment units"
                                            value={stockQty}
                                            onChange={(e) => {
                                                const val = e.target.value.replace(/[^0-9]/g, '');
                                                if (val.includes("-") || Number(val) < 0) {
                                                    return;
                                                }
                                                setStockQty(val);
                                                if (errors.stockQty) {
                                                    setErrors(prev => ({ ...prev, stockQty: "" }));
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "-" || e.key === "e" || e.key === "+") {
                                                    e.preventDefault();
                                                }
                                            }}
                                            style={{ borderColor: errors.stockQty ? "#d93939" : "#dee2e6" }}
                                        />
                                        {errors.stockQty && <small className="text-danger d-block mt-4">{errors.stockQty}</small>}
                                        <div className="form-text mt-8 d-flex align-items-center gap-2 text-secondary-light" style={{ fontSize: "13px" }}>
                                            <span>Current Stock: <strong className="text-dark">{product.attributes[selectedVariantIndex].stock}</strong></span>
                                            <span className="text-secondary-300">|</span>
                                            <span>Target Stock: <strong className={stockType === "add" ? "text-success-600" : "text-danger-600"}>
                                                {stockType === "add" 
                                                    ? (Number(product.attributes[selectedVariantIndex].stock) || 0) + (Number(stockQty) || 0) 
                                                    : Math.max(0, (Number(product.attributes[selectedVariantIndex].stock) || 0) - (Number(stockQty) || 0))}
                                            </strong></span>
                                        </div>
                                    </div>

                                    {/* Reference */}
                                    <div className="col-12 mt-24">
                                        <label className="form-label fw-bold text-secondary-light mb-8">Description / Reference (Optional)</label>
                                        <input
                                            type="text"
                                            className="form-control radius-8 h-45-px"
                                            placeholder="e.g. Audit correction, restock, returned items"
                                            value={stockDesc}
                                            onChange={(e) => setStockDesc(e.target.value)}
                                            style={{ borderColor: "#dee2e6" }}
                                        />
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Buttons */}
                        <div className="d-flex justify-content-end gap-3 mt-40 border-top pt-24">
                            <Link to="/products" className="btn btn-outline-secondary px-32 py-12 radius-8 fw-semibold" style={{ minWidth: "140px" }}>
                                Cancel
                            </Link>
                            {hasPermission("Add Stock", "edit") && (
                                <button 
                                    type="submit" 
                                    className="btn btn-primary px-32 py-12 radius-8 fw-semibold" 
                                    style={{ backgroundColor: "#364e3d", borderColor: "#364e3d", minWidth: "160px" }}
                                    disabled={stockLoading || selectedVariantIndex === null}
                                >
                                    {stockLoading ? "Updating..." : "Confirm Update"}
                                </button>
                            )}
                        </div>
                    </form>
                ) : (
                    <div className="text-center py-5 text-secondary-light">Product details not found.</div>
                )}
            </div>
        </div>
    );
};

export default ProductStockUpdateLayer;
